import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { AuthGate } from "./components/AuthGate";
import { RouteFallback } from "./components/RouteFallback";
import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { Pricing } from "./pages/Pricing";
import { Blog } from "./pages/Blog";
import { BlogPost } from "./pages/gids/Guide";
import { Author } from "./pages/Author";
// Public Contact + Status pages. Eager (not lazy) so the build-time prerender's
// synchronous renderToString can emit their HTML — both are public, no auth.
import { Contact } from "./pages/Contact";
import { Status } from "./pages/Status";
import { BlogChrome } from "./components/BlogChrome";
import { ScrollToTop } from "./components/ScrollToTop";
import { consumerLink } from "./lib/links";

// Route-level code-splitting. The landing page, app shell, and auth guard stay
// eagerly imported (above) so first paint and the login flow are not gated on a
// second chunk fetch. Everything routed *inside* the shell — plus the heavy
// public routes (deal canvas, e-sign, client portal) — is lazy-loaded so the
// libraries they pull (@xyflow/react, yjs, Tiptap, Leaflet, pdfjs-dist) land in
// their own chunks instead of the initial bundle. All page components are named
// exports, hence the `.then(m => ({ default: m.X }))` mapping React.lazy needs.
const Dashboard = lazy(() =>
  import("./pages/Dashboard").then((m) => ({ default: m.Dashboard })),
);
const Clients = lazy(() =>
  import("./pages/Clients").then((m) => ({ default: m.Clients })),
);
const Agenda = lazy(() =>
  import("./pages/Agenda").then((m) => ({ default: m.Agenda })),
);
const Sellers = lazy(() =>
  import("./pages/Sellers").then((m) => ({ default: m.Sellers })),
);
const SellerDetail = lazy(() =>
  import("./pages/SellerDetail").then((m) => ({ default: m.SellerDetail })),
);
const Properties = lazy(() =>
  import("./pages/Properties").then((m) => ({ default: m.Properties })),
);
const PropertyDetail = lazy(() =>
  import("./pages/PropertyDetail").then((m) => ({ default: m.PropertyDetail })),
);
const DealView = lazy(() =>
  import("./pages/DealView").then((m) => ({ default: m.DealView })),
);
const Documents = lazy(() =>
  import("./pages/Documents").then((m) => ({ default: m.Documents })),
);
const Team = lazy(() =>
  import("./pages/Team").then((m) => ({ default: m.Team })),
);
const SettingsLayout = lazy(() =>
  import("./pages/Settings").then((m) => ({ default: m.SettingsLayout })),
);
const OrganizationSettings = lazy(() =>
  import("./pages/Settings").then((m) => ({ default: m.OrganizationSettings })),
);
const MembersSettings = lazy(() =>
  import("./pages/Settings").then((m) => ({ default: m.MembersSettings })),
);
const BillingSettings = lazy(() =>
  import("./pages/Settings").then((m) => ({ default: m.BillingSettings })),
);
const AccountSettings = lazy(() =>
  import("./pages/Settings").then((m) => ({ default: m.AccountSettings })),
);
const JoinInvite = lazy(() =>
  import("./pages/JoinInvite").then((m) => ({ default: m.JoinInvite })),
);
const VerifyEmail = lazy(() =>
  import("./pages/VerifyEmail").then((m) => ({ default: m.VerifyEmail })),
);
const EmailVerified = lazy(() =>
  import("./pages/EmailVerified").then((m) => ({ default: m.EmailVerified })),
);
const ForgotPassword = lazy(() =>
  import("./pages/ForgotPassword").then((m) => ({ default: m.ForgotPassword })),
);
const ResetPassword = lazy(() =>
  import("./pages/ResetPassword").then((m) => ({ default: m.ResetPassword })),
);
const GetStarted = lazy(() =>
  import("./pages/GetStarted").then((m) => ({ default: m.GetStarted })),
);
const SpaceHome = lazy(() =>
  import("./pages/SpaceHome").then((m) => ({ default: m.SpaceHome })),
);
const Space = lazy(() =>
  import("./pages/Space").then((m) => ({ default: m.Space })),
);
const Sign = lazy(() =>
  import("./pages/Sign").then((m) => ({ default: m.Sign })),
);

// Public blog/guides ported from the consumer site. Lazy so the 22 guides plus
// react-helmet-async land in their own chunk, off the app's critical path.
// Blog index, article renderer (BlogPost) and author page are imported eagerly
// (not lazy) so the build-time prerender's synchronous renderToString emits
// their full HTML for SEO — React.lazy suspends under renderToString. The
// articles themselves stay code-split: BlogPost renders them from the guide
// registry, whose loaders are dynamic imports (one chunk per article).

// In-app legal/policy pages (self-hosted in the Pro app — no consumer-site
// dependency). Lazy so the policy text lands in its own chunk.
const LegalPage = lazy(() =>
  import("./pages/LegalPage").then((m) => ({ default: m.LegalPage })),
);

/** Legacy /gids/<slug> → canonical /blog/<slug> (mirrors the consumer site). */
function GidsSlugRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={slug ? `/blog/${slug}` : "/blog"} replace />;
}

/**
 * The ported guides funnel readers to the consumer report/calculator tools,
 * which live on the public site (biedradar.nl), not in this app. Route those
 * in-article links out instead of bouncing them through the SPA catch-all.
 */
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

export function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        {/* "Start now free" funnel — full-screen, unclosable signup + onboarding
            wizard the marketing CTAs route to (replaces the old /app jump). */}
        <Route path="/start" element={<GetStarted />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/email-verified" element={<EmailVerified />} />
        <Route path="/join/:token" element={<JoinInvite />} />

        <Route
          path="/app"
          element={
            <AuthGate>
              <AppShell />
            </AuthGate>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="clients" element={<Clients />} />
          <Route path="agenda" element={<Agenda />} />
          <Route path="sellers" element={<Sellers />} />
          <Route path="sellers/:agentId" element={<SellerDetail />} />
          <Route path="properties" element={<Properties />} />
          <Route path="properties/:stepId" element={<PropertyDetail />} />
          <Route path="deals/:dealId" element={<DealView />} />
          <Route path="documents" element={<Documents />} />
          <Route path="team" element={<Team />} />
          {/* Personal account settings — a top-level destination, separate from
              the organization (workspace) settings below. */}
          <Route path="account" element={<AccountSettings />} />
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="organization" replace />} />
            <Route path="organization" element={<OrganizationSettings />} />
            <Route path="members" element={<MembersSettings />} />
            <Route path="billing" element={<BillingSettings />} />
            {/* Legacy deep-link → new top-level account route. */}
            <Route path="account" element={<Navigate to="/app/account" replace />} />
          </Route>
        </Route>

        {/* Client portal "the space" (no app shell). Durable client logins:
            /space is the returning-client home, /space/deal/:dealId a granted
            deal, /space/:token a fresh invite link (signs in → accepts → grants).
            The literal `deal` segment outranks the `:token` param in v6. */}
        <Route path="/space" element={<SpaceHome />} />
        <Route path="/space/deal/:dealId" element={<Space />} />
        <Route path="/space/:token" element={<Space />} />
        {/* E-sign stays purely token-gated (no account needed to sign). */}
        <Route path="/sign/:token" element={<Sign />} />

        {/* Public blog/guides (ported from the consumer site) — a light
            gov-style surface with its own minimal chrome, not the app shell. */}
        <Route element={<BlogChrome />}>
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/auteur/:slug" element={<Author />} />
        </Route>
        <Route path="/gids" element={<Navigate to="/blog" replace />} />
        <Route path="/gids/:slug" element={<GidsSlugRedirect />} />

        {/* Public Contact + first-party Status pages — no auth, own chrome. */}
        <Route path="/contact" element={<Contact />} />
        <Route path="/status" element={<Status />} />

        {/* In-app legal/policy pages — comprehensive, self-hosted, AVG-aware. */}
        <Route path="/privacy" element={<LegalPage slug="privacy" />} />
        <Route path="/terms" element={<LegalPage slug="terms" />} />
        <Route path="/algemene-voorwaarden" element={<Navigate to="/terms" replace />} />
        <Route path="/security" element={<LegalPage slug="security" />} />
        <Route path="/cookies" element={<LegalPage slug="cookies" />} />
        <Route path="/disclaimer" element={<LegalPage slug="disclaimer" />} />

        {/* Consumer funnel targets referenced by the ported guides — they live
            on the public site, so send visitors there. */}
        <Route
          path="/get-report"
          element={<ExternalRedirect to={consumerLink("/get-report")} />}
        />
        <Route
          path="/calculator"
          element={<ExternalRedirect to={consumerLink("/calculator")} />}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
